import type { IGroup } from '@bridebook/models/source/models/Countries/Groups.types';
import type { ITask } from '@bridebook/models/source/models/Countries/Tasks.types';
import type { BlueprintGroup, BlueprintTask, PlanningBlueprint } from './types';

// Some CMS task names have absolute links into the app, dev hosts included. The model
// and the couple see them, so strip the origin and keep the path, which works everywhere.
const BRIDEBOOK_ORIGIN =
  /https?:\/\/[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:bridebook\.(?:com|co\.uk)|vercel\.app)/gi;

export const stripAbsoluteBridebookLinks = (text: string): string =>
  text.replace(BRIDEBOOK_ORIGIN, '');

/**
 * Turns the raw country collections into the blueprint the planner reads. Keeps only
 * `active` tasks, as the real checklist setup does.
 *
 * Returns null until both collections load, since an empty blueprint and a loading one
 * look the same later.
 */
export const normaliseBlueprint = ({
  countryTasks,
  countryGroups,
  country,
  taskName,
}: {
  countryTasks: Record<string, ITask> | undefined;
  countryGroups: Record<string, IGroup> | undefined;
  country: string | null | undefined;
  // A task's name as the checklist page shows it, by its translation key.
  taskName?: (i18nKey: string | null) => string | undefined;
}): PlanningBlueprint | null => {
  if (!(countryTasks && countryGroups && country)) {
    return null;
  }

  const tasks: Record<string, BlueprintTask> = {};
  for (const task of Object.values(countryTasks)) {
    if (task.active !== true || !task.group) {
      continue;
    }
    const i18nKey = task.i18nKey ?? null;
    tasks[task.id] = {
      id: task.id,
      group: task.group,
      name: taskName?.(i18nKey) ?? stripAbsoluteBridebookLinks(task.name ?? task.id),
      period: task.period ?? '-P',
      order: task.order ?? 0,
      i18nKey,
      roles: task.roles ?? null,
    };
  }

  const groups: Record<string, BlueprintGroup> = {};
  for (const group of Object.values(countryGroups)) {
    groups[group.id] = {
      id: group.id,
      name: group.name ?? group.id,
      intro: stripAbsoluteBridebookLinks(group.intro ?? ''),
      tip: stripAbsoluteBridebookLinks(group.tip ?? ''),
      supplierType: group.supplierType ?? null,
      i18nKey: group.i18nKey ?? null,
    };
  }

  if (Object.keys(tasks).length === 0 || Object.keys(groups).length === 0) {
    return null;
  }

  return { tasks, groups, country };
};

// Groups with at least one template task. The same collection also holds budget
// categories and other entries, so a group with no task is not a checklist category.
export const populatedGroupIds = (blueprint: PlanningBlueprint): string[] => {
  const withTasks = new Set(Object.values(blueprint.tasks).map((task) => task.group));
  return Object.keys(blueprint.groups).filter((groupId) => withTasks.has(groupId));
};
